import type {
  GuideTourDefinition,
  GuideTourRegistry,
  GuideTourStep,
} from '../types'
import {
  getGuideTourProgress,
  isGuideTourSeen,
  type GuideTourProgress,
} from '../persistence'
import {
  findNextAvailableGuideTourStepIndex,
  hasAvailableGuideTourSteps,
} from '../utils/tourRuntime'
import {
  isGuideTourStepGatesReadyForActivation,
  isGuideTourStepTargetPresent,
} from './activation'

const escapeGuideTourRouteSegment = (segment: string): string =>
  segment.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`)

const toGuideTourRoutePattern = (tourRoute: string): RegExp => {
  const segments = tourRoute.split('/').filter(Boolean)
  const pattern = segments
    .map(segment =>
      segment.startsWith(':') ? '[^/]+' : escapeGuideTourRouteSegment(segment)
    )
    .join('/')

  return new RegExp(`^/${pattern}$`)
}

/**
 * Matches a tour route against the current pathname.
 *
 * - Static routes: exact match or nested paths (`/orders` → `/orders/123`).
 * - Dynamic routes: React Router-style params (`/product/:id/details`).
 */
export const matchGuideTourRoute = (
  tourRoute: string,
  pathname: string
): boolean => {
  if (tourRoute.includes(':')) {
    return toGuideTourRoutePattern(tourRoute).test(pathname)
  }

  return pathname === tourRoute || pathname.startsWith(`${tourRoute}/`)
}

export const isGuideTourEnabled = (tour: GuideTourDefinition): boolean =>
  tour.enabled !== false

export const isGuideTourAvailable = (
  tour: GuideTourDefinition,
  now: Date = new Date()
): boolean => isGuideTourEnabled(tour) && hasAvailableGuideTourSteps(tour, now)

export const GUIDE_TOUR_TIERS = [
  'page',
  'onboard',
  'feature',
  'campaign',
] as const

export type GuideTourTier = (typeof GUIDE_TOUR_TIERS)[number]

/** Higher value wins when multiple tours match the same route. */
export const GUIDE_TOUR_TIER_PRIORITY: Record<GuideTourTier, number> = {
  page: 1,
  onboard: 2,
  feature: 4,
  campaign: 5,
}

/**
 * Manual `priority` in JSON is a tie-breaker within the same tier only (0–9).
 * Effective order = tier base × 10 + offset. A page tour can never outrank a feature.
 */
export const GUIDE_TOUR_PRIORITY_OFFSET_MIN = 0
export const GUIDE_TOUR_PRIORITY_OFFSET_MAX = 9

export const getGuideTourTierBasePriority = (tier: GuideTourTier): number =>
  GUIDE_TOUR_TIER_PRIORITY[tier] * 10

export const getGuideTourPriorityOffset = (tour: GuideTourDefinition): number =>
  tour.priority ?? GUIDE_TOUR_PRIORITY_OFFSET_MIN

export const getGuideTourStepRoute = (
  tour: GuideTourDefinition,
  step: GuideTourStep
): string | undefined => step.route ?? tour.route

export const getGuideTourDistinctRoutes = (
  tour: GuideTourDefinition
): string[] => {
  const routes = tour.steps
    .map(step => getGuideTourStepRoute(tour, step))
    .filter((route): route is string => Boolean(route))

  return [...new Set(routes)]
}

export const isMultiRouteGuideTour = (tour: GuideTourDefinition): boolean =>
  getGuideTourDistinctRoutes(tour).length > 1

/**
 * Infers the tour tier from JSON shape (folder conventions reflected at runtime):
 * - `campaignId` → campaign (multi-page release)
 * - `type: feature` → feature (single-screen novelty)
 * - `type: onboard` + multiple routes → onboard (platform walkthrough)
 * - `type: onboard` + single route → page (first visit to that screen)
 */
export const getGuideTourTier = (tour: GuideTourDefinition): GuideTourTier => {
  if (tour.campaignId) {
    return 'campaign'
  }

  if (tour.type === 'feature') {
    return 'feature'
  }

  if (isMultiRouteGuideTour(tour)) {
    return 'onboard'
  }

  return 'page'
}

export const getGuideTourDefaultPriority = (
  tour: GuideTourDefinition
): number =>
  getGuideTourTierBasePriority(getGuideTourTier(tour)) +
  getGuideTourPriorityOffset(tour)

export const tourMatchesPathname = (
  tour: GuideTourDefinition,
  pathname: string
): boolean =>
  getGuideTourDistinctRoutes(tour).some(route =>
    matchGuideTourRoute(route, pathname)
  )

export const getInitialStepIndexForPathname = (
  tour: GuideTourDefinition,
  pathname: string
): number => {
  const matchingIndex = tour.steps.findIndex(step => {
    const route = getGuideTourStepRoute(tour, step)

    return route ? matchGuideTourRoute(route, pathname) : false
  })

  const startIndex = matchingIndex === -1 ? 0 : matchingIndex
  const resolvedIndex = findNextAvailableGuideTourStepIndex(tour, startIndex)

  return resolvedIndex ?? startIndex
}

export const isGuideTourStepOnPathname = (
  tour: GuideTourDefinition,
  stepIndex: number,
  pathname: string
): boolean => {
  const step = tour.steps[stepIndex]

  if (!step) {
    return false
  }

  const route = getGuideTourStepRoute(tour, step)

  return route ? matchGuideTourRoute(route, pathname) : false
}

export type GuideTourActivationCandidate = {
  tour: GuideTourDefinition
  stepIndex: number
  viewedStepCount?: number
}

export const getPendingGuideToursForPathname = (
  pathname: string,
  registry: GuideTourRegistry,
  isSeen: typeof isGuideTourSeen = isGuideTourSeen
): GuideTourActivationCandidate[] =>
  registry
    .listGuideTours()
    .filter(
      tour =>
        isGuideTourAvailable(tour) &&
        !isSeen(tour.id, tour.version) &&
        !getGuideTourProgress(tour.id, tour.version) &&
        tourMatchesPathname(tour, pathname)
    )
    .map(tour => ({
      tour,
      stepIndex: getInitialStepIndexForPathname(tour, pathname),
    }))
    .sort((left, right) => {
      const priorityDiff =
        getGuideTourDefaultPriority(right.tour) -
        getGuideTourDefaultPriority(left.tour)

      if (priorityDiff !== 0) {
        return priorityDiff
      }

      return left.tour.id.localeCompare(right.tour.id)
    })

export const getGuideToursForPathname = (
  pathname: string,
  registry: GuideTourRegistry
): readonly GuideTourDefinition[] =>
  getPendingGuideToursForPathname(pathname, registry).map(
    candidate => candidate.tour
  )

export const getPrimaryGuideTourForPathname = (
  pathname: string,
  registry: GuideTourRegistry
): GuideTourDefinition | undefined =>
  getPendingGuideToursForPathname(pathname, registry)[0]?.tour

export const hasSeenGuideTourOnPathname = (
  pathname: string,
  registry: GuideTourRegistry,
  isSeen: typeof isGuideTourSeen = isGuideTourSeen
): boolean =>
  registry
    .listGuideTours()
    .some(
      tour =>
        isGuideTourAvailable(tour) &&
        tourMatchesPathname(tour, pathname) &&
        isSeen(tour.id, tour.version)
    )

export type GuideTourProgressCandidate = GuideTourProgress & {
  tour: GuideTourDefinition
}

export const findInProgressGuideTour = (
  registry: GuideTourRegistry,
  isSeen: typeof isGuideTourSeen = isGuideTourSeen
): GuideTourProgressCandidate | null => {
  for (const tour of registry.listGuideTours()) {
    if (!isGuideTourAvailable(tour) || isSeen(tour.id, tour.version)) {
      continue
    }

    const progress = getGuideTourProgress(tour.id, tour.version)

    if (progress) {
      return {
        tour,
        ...progress,
      }
    }
  }

  return null
}

export type GuideTourRuntimeStateSnapshot = {
  status: string
  activeTourId: string | null
}

export const selectGuideTourActivationCandidate = (
  pathname: string,
  registry: GuideTourRegistry,
  isSeen: typeof isGuideTourSeen = isGuideTourSeen
): GuideTourActivationCandidate | null => {
  const pending = getPendingGuideToursForPathname(pathname, registry, isSeen)

  const readyCandidate = pending.find(candidate => {
    const { tour, stepIndex } = candidate

    if (!isGuideTourStepGatesReadyForActivation(tour, stepIndex)) {
      return true
    }

    if (!isGuideTourStepTargetPresent(tour, stepIndex)) {
      return false
    }

    return true
  })

  return readyCandidate ?? pending[0] ?? null
}

export const selectGuideTourActivation = (
  pathname: string,
  registry: GuideTourRegistry,
  runtime: GuideTourRuntimeStateSnapshot,
  isSeen: typeof isGuideTourSeen = isGuideTourSeen
): GuideTourActivationCandidate | null => {
  if (runtime.status === 'active' && runtime.activeTourId) {
    return null
  }

  return selectGuideTourActivationCandidate(pathname, registry, isSeen)
}
